import { useState, useEffect } from 'react';
import logger from '../utils/logger';

interface Auth0Config {
    domain: string;
    clientId: string;
    audience: string;
    environment: string;
}

interface UseAuth0ConfigResult {
    config: Auth0Config | null;
    loading: boolean;
    error: string | null;
}

export const useAuth0Config = (): UseAuth0ConfigResult => {
    const [config, setConfig] = useState<Auth0Config | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAuth0Config = async () => {
            try {
                console.log('🔐 USE-AUTH0-CONFIG: Starting Auth0 config fetch', {
                    timestamp: new Date().toISOString(),
                    currentUrl: window.location.href,
                    origin: window.location.origin
                });

                logger.debug('🔐 Fetching Auth0 configuration from /api/auth0-config');

                const response = await fetch('/api/auth0-config', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                console.log('🔐 USE-AUTH0-CONFIG: Response received', {
                    status: response.status,
                    statusText: response.statusText,
                    ok: response.ok,
                    headers: Object.fromEntries(response.headers.entries()),
                    timestamp: new Date().toISOString()
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();

                console.log('🔐 USE-AUTH0-CONFIG: Data parsed', {
                    success: data.success,
                    hasConfig: !!data.config,
                    configKeys: data.config ? Object.keys(data.config) : [],
                    message: data.message,
                    timestamp: new Date().toISOString()
                });

                if (!data.success || !data.config) {
                    throw new Error(data.message || 'Invalid Auth0 configuration response');
                }

                const { domain, clientId, audience, environment } = data.config;

                // Validate required fields
                if (!domain || !clientId || !audience) {
                    throw new Error(`Missing Auth0 configuration: domain=${domain}, clientId=${clientId}, audience=${audience}`);
                }

                logger.info('✅ Auth0 configuration loaded successfully', {
                    domain,
                    clientId: clientId.substring(0, 8) + '...[REDACTED]',
                    audience,
                    environment
                });

                console.log('🔐 USE-AUTH0-CONFIG: Configuration validated and set', {
                    domain,
                    clientId: clientId.substring(0, 8) + '...[REDACTED]',
                    audience,
                    environment,
                    timestamp: new Date().toISOString()
                });

                setConfig({ domain, clientId, audience, environment });
                setError(null);

            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                logger.error('❌ Failed to fetch Auth0 configuration:', errorMessage);

                console.log('🔐 USE-AUTH0-CONFIG: Error occurred', {
                    error: errorMessage,
                    errorType: err instanceof Error ? err.constructor.name : typeof err,
                    stack: err instanceof Error ? err.stack : undefined,
                    timestamp: new Date().toISOString()
                });

                setError(errorMessage);
                setConfig(null);
            } finally {
                setLoading(false);
                console.log('🔐 USE-AUTH0-CONFIG: Fetch complete', {
                    timestamp: new Date().toISOString()
                });
            }
        };

        fetchAuth0Config();
    }, []);

    return { config, loading, error };
};
